import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import FormValidation from "../../../Common/FormValidation";
import Http from "../../../Common/Http";
import IsLogin from "../../../Common/IsLogin";
import { LoaderComponent } from "../../../Common/LoaderComponent";
import { ProgressSpinner } from "primereact/progressspinner";
import Select from "react-select";
const DepositEdit = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [inputs, setInputs] = useState({});
  const [error, setError] = useState({});
  const [loading, setLoading] = useState(true);
  const [banks, setBanks] = useState([]);
  const [bankBalance, setBankBalance] = useState(null);
  const [loadingBalance, setLoadingBalance] = useState(false);
  const [balanceError, setBalanceError] = useState("");
  const [counterStaff, setCounterStaff] = useState([]);
  const [depositCategories, setDepositCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [staffData, setStaffData] = useState([]);
  const [loadingStaff, setLoadingStaff] = useState(false);
  const [vendorData, setVendorData] = useState([]);
  const [loadingVendors, setLoadingVendors] = useState(false);
  const [shopData, setShopData] = useState([]);
  const [loadingShops, setLoadingShops] = useState(false);
  const shopDataFetched = useRef(false);
  const [departmentData, setDepartmentData] = useState([]);
  const [loadingDepartments, setLoadingDepartments] = useState(false);
  const departmentDataFetched = useRef(false);

  useEffect(() => {
    Promise.all([fetchDeposit(), fetchBanks(), fetchCounterStaff(), fetchDepositCategories()]);
  }, [id]);

  // Fetch staff data when needed
  useEffect(() => {
    if ([2, 3, 4, 7].includes(inputs.category_id) && staffData.length === 0 && !loadingStaff) {
      fetchStaffData();
    }
  }, [inputs.category_id, staffData.length, loadingStaff]);

  // Fetch vendor data when needed
  useEffect(() => {
    if (inputs.category_id === 5 && vendorData.length === 0 && !loadingVendors) {
      fetchVendorData();
    }
  }, [inputs.category_id, vendorData.length, loadingVendors]);

  // Fetch shop data when needed
  useEffect(() => {
    if (inputs.category_id === 6 && !shopDataFetched.current && !loadingShops) {
      fetchShopData();
    }
  }, [inputs.category_id]);

  // Fetch department data when needed
  useEffect(() => {
    if (inputs.category_id === 1 && !departmentDataFetched.current && !loadingDepartments) {
      fetchDepartmentData();
    }
  }, [inputs.category_id]);

  const fetchCounterStaff = async () => {
    return Http.get("/counter_staff")
      .then((res) => {
        setCounterStaff(res.data);
      })
      .catch((error) => {
        console.error("Fetch counter staff error:", error);
      });
  };

  const fetchStaffData = () => {
    setLoadingStaff(true);
    Http.get("/staff2")
      .then((res) => {
        setStaffData(res.data);
      })
      .catch((error) => {
        console.error("Fetch staff data error:", error);
      })
      .finally(() => {
        setLoadingStaff(false);
      });
  };

  const fetchVendorData = () => {
    setLoadingVendors(true);
    Http.get("/vendorSelect2")
      .then((res) => {
        setVendorData(res.data);
      })
      .catch((error) => {
        console.error("Fetch vendor data error:", error);
      })
      .finally(() => {
        setLoadingVendors(false);
      });
  };

  const fetchShopData = () => {
    setLoadingShops(true);
    Http.get("/accounts/shops-2")
      .then((res) => {
        setShopData(res.data);
        shopDataFetched.current = true;
      })
      .catch((error) => {
        console.error("Fetch shop data error:", error);
      })
      .finally(() => {
        setLoadingShops(false);
      });
  };

  const fetchDepartmentData = () => {
    setLoadingDepartments(true);
    Http.get("/department2")
      .then((res) => {
        setDepartmentData(res.data);
        departmentDataFetched.current = true;
      })
      .catch((error) => {
        console.error("Fetch department data error:", error);
      })
      .finally(() => {
        setLoadingDepartments(false);
      });
  };

  const fetchDepositCategories = async () => {
    setLoadingCategories(true);
    return Http.get("/accounts/deposit-categories")
      .then((res) => {
        setDepositCategories(res.data);
      })
      .catch((error) => {
        console.error("Fetch deposit categories error:", error);
      })
      .finally(() => {
        setLoadingCategories(false);
      });
  };


  const fetchDeposit = async () => {
    return Http.get(`/accounts/deposits/${id}`)
      .then((res) => {
        const data = res.data.data;
        // Convert status to lowercase if it exists
        if (data.status) {
          data.status = data.status.toLowerCase();
        }
        setInputs(data);
        // Fetch bank balance for the current bank
        if (data.bank_id) {
          fetchBankBalance(data.bank_id);
        }
      })
      .catch((error) => {
        console.error("Fetch error:", error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const fetchBanks = () => {
    return Http.get("/accounts/banks")
      .then((res) => {
        setBanks(res.data);
      })
      .catch((error) => {
        console.error("Fetch error:", error);
      });
  };

  const fetchBankBalance = (bankId) => {
    if (!bankId) {
      setBankBalance(null);
      setBalanceError("");
      return;
    }

    setLoadingBalance(true);
    setBalanceError("");
    Http.get(`/accounts/get-single-bank-ledger-summary/${bankId}`)
      .then((res) => {
        if (res.data.success) {
          setBankBalance(res.data.data);
        } else {
          setBalanceError("Failed to fetch bank balance");
        }
      })
      .catch((error) => {
        console.error("Balance fetch error:", error);
        setBalanceError("Error fetching bank balance");
        setBankBalance(null);
      })
      .finally(() => {
        setLoadingBalance(false);
      });
  };

  const handleChange = (e) => {
    const name = e.target.name;
    const value = e.target.value;
    setInputs((values) => ({ ...values, [name]: value }));

    // Fetch bank balance when bank is selected
    if (name === "bank_id") {
      fetchBankBalance(value);
    }
  };

  const formSubmit = () => {
    Http.put(`/accounts/deposits/${id}`, inputs)
      .then((res) => {
        navigate("/deposits", {
          state: { message: res.data.message, type: "success" },
        });
      })
      .catch(function (e) {
        setError(e.response.data.errors);
      });
  };

  return (
    <div>
      <IsLogin></IsLogin>
      <LoaderComponent />
      <div className="am-mainpanel">
        <div className="am-pagetitle">
          <h5 className="am-title">Edit Deposit</h5>
        </div>
        <div className="am-pagebody">
          <div className="card pd-20 pd-sm-40">
            <h6 className="card-body-title mg-sm-b-30">Deposit Information</h6>
            <div className="row">
              <div className="col-lg">
                <FormValidation error={error} />
              </div>
            </div>

            {loading ? (
              <ProgressSpinner />
            ) : (
              <>

                <div className="row">
                  <div className="col-lg">
                    <label>Deposit Category <span className="text-danger">*</span></label>
                    <Select
                      className="basic-single"
                      classNamePrefix="select"
                      name="category_id"
                      value={
                        depositCategories
                          .map((category) => ({
                            value: category.id,
                            label: category.name,
                          }))
                          .find((option) => option.value === inputs.category_id) ||
                        null
                      }
                      options={depositCategories.map((category) => ({
                        value: category.id,
                        label: category.name,
                      }))}
                      onChange={(selectedOption) => {
                        setInputs((prev) => ({
                          ...prev,
                          category_id: selectedOption.value,
                        }));
                      }}
                      placeholder="Select Deposit Category"
                      isLoading={loadingCategories}
                    />
                  </div>

                  {/* Conditional Staff Dropdown */}
                  {[2, 3, 4, 7].includes(inputs.category_id) && (
                    <div className="col-lg">
                      <label>
                        Staff List <span className="text-danger">*</span>
                      </label>
                      <Select
                        className="basic-single"
                        classNamePrefix="select"
                        name="staff_id"
                        value={
                          staffData
                            .map((staff) => ({
                              value: staff.value,
                              label: staff.label,
                            }))
                            .find((option) => option.value === inputs.staff_id) ||
                          null
                        }
                        options={staffData.map((staff) => ({
                          value: staff.value,
                          label: staff.label,
                        }))}
                        onChange={(selectedOption) => {
                          setInputs((prev) => ({
                            ...prev,
                            staff_id: selectedOption.value,
                          }));
                        }}
                        placeholder="Select Staff"
                        isLoading={loadingStaff}
                      />
                    </div>
                  )}

                  {/* Conditional Department Dropdown */}
                  {inputs.category_id === 1 && (
                    <div className="col-lg">
                      <label>
                        Department <span className="text-danger">*</span>
                      </label>
                      <Select
                        className="basic-single"
                        classNamePrefix="select"
                        name="department_id"
                        value={
                          departmentData
                            .map((department) => ({
                              value: department.value,
                              label: department.label,
                            }))
                            .find((option) => option.value === inputs.department_id) ||
                          null
                        }
                        options={departmentData.map((department) => ({
                          value: department.value,
                          label: department.label,
                        }))}
                        onChange={(selectedOption) => {
                          setInputs((prev) => ({
                            ...prev,
                            department_id: selectedOption.value,
                          }));
                        }}
                        placeholder="Select Department"
                        isLoading={loadingDepartments}
                      />
                    </div>
                  )}

                  {/* Conditional Shop Dropdown */}
                  {inputs.category_id === 6 && (
                    <div className="col-lg">
                      <label>
                        Shop <span className="text-danger">*</span>
                      </label>
                      <Select
                        className="basic-single"
                        classNamePrefix="select"
                        name="shop_id"
                        value={
                          shopData
                            .map((shop) => ({
                              value: shop.value,
                              label: shop.label,
                            }))
                            .find((option) => option.value === inputs.shop_id) ||
                          null
                        }
                        options={shopData.map((shop) => ({
                          value: shop.value,
                          label: shop.label,
                        }))}
                        onChange={(selectedOption) => {
                          setInputs((prev) => ({
                            ...prev,
                            shop_id: selectedOption.value,
                          }));
                        }}
                        placeholder="Select Shop"
                        isLoading={loadingShops}
                      />
                    </div>
                  )}

                  {/* Conditional Vendor Dropdown */}
                  {inputs.category_id === 5 && (
                    <div className="col-lg">
                      <label>
                        Vendor <span className="text-danger">*</span>
                      </label>
                      <Select
                        className="basic-single"
                        classNamePrefix="select"
                        name="vendor_id"
                        value={
                          vendorData
                            .map((vendor) => ({
                              value: vendor.value,
                              label: vendor.label,
                            }))
                            .find((option) => option.value === inputs.vendor_id) ||
                          null
                        }
                        options={vendorData.map((vendor) => ({
                          value: vendor.value,
                          label: vendor.label,
                        }))}
                        onChange={(selectedOption) => {
                          setInputs((prev) => ({
                            ...prev,
                            vendor_id: selectedOption.value,
                          }));
                        }}
                        placeholder="Select Vendor"
                        isLoading={loadingVendors}
                      />
                    </div>
                  )}

                  <div className="col-lg">
                    <label>Amount <span className="text-danger">*</span></label>
                    <input
                      type="number"
                      className="form-control"
                      name="amount"
                      value={inputs.amount || ""}
                      onChange={handleChange}
                      placeholder="Enter Amount"
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-lg">
                    <label>Bank <span className="text-danger">*</span></label>
                    <select
                      className="form-control"
                      name="bank_id"
                      value={inputs.bank_id || ""}
                      onChange={handleChange}
                    >
                      <option value="">Select Bank</option>
                      {banks.map((bank) => (
                        <option key={bank.id} value={bank.id}>
                          {bank.name} - {bank.account_number}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-lg">
                    <label>Deposit Date <span className="text-danger">*</span></label>
                    <input
                      type="date"
                      className="form-control"
                      name="date"
                      value={inputs.date ? inputs.date.split('T')[0] : ""}
                      onChange={handleChange}
                    />
                  </div>
                </div>

                {/* Bank Balance Display */}
                {inputs.bank_id && (
                  <div className="row mt-2">
                    <div className="col-lg-6">
                      <div className="alert alert-info" style={{ marginBottom: '15px' }}>
                        <strong>Bank Balance Information:</strong>
                        {loadingBalance ? (
                          <div className="d-flex align-items-center mt-2">
                            <div className="spinner-border spinner-border-sm me-2" role="status">
                              <span className="sr-only">Loading...</span>
                            </div>
                            <span>Loading bank balance...</span>
                          </div>
                        ) : balanceError ? (
                          <div className="text-danger mt-2">
                            <i className="fa fa-exclamation-triangle me-1"></i>
                            {balanceError}
                          </div>
                        ) : bankBalance ? (
                          <div className="mt-2">
                            <div className="row">
                              <div className="col-md-4">
                                <small className="text-muted">Bank Name:</small>
                                <div className="font-weight-bold">{bankBalance.name}</div>
                              </div>
                              <div className="col-md-4">
                                <small className="text-muted">Account Number:</small>
                                <div className="font-weight-bold">{bankBalance.account_number}</div>
                              </div>
                              <div className="col-md-4">
                                <small className="text-muted">Current Balance:</small>
                                <div className="font-weight-bold text-success">
                                  ৳ {bankBalance.total_balance}
                                </div>
                              </div>
                            </div>
                          </div>
                        ) : null}
                      </div>
                    </div>
                  </div>
                )}

                {/* Counter Staff Dropdown - Exclude category_ids 1, 2, 3, 4, 6, 7, 5 */}
                {![1, 2, 3, 4, 6, 7, 5].includes(inputs.category_id) && (
                  <div className="row mt-3">
                    <div className="col-lg">
                      <label>
                        Counter Staff <span className="text-danger">*</span>
                      </label>
                      <Select
                        className="basic-single"
                        classNamePrefix="select"
                        name="counter_id"
                        value={
                          counterStaff
                            .map((staff) => ({
                              value: staff.id,
                              label: `${staff.name} - ${staff.card_no}`,
                            }))
                            .find((option) => option.value === inputs.counter_id) ||
                          null
                        }
                        options={counterStaff
                          .map((staff) => ({
                            value: staff.id,
                            label: `${staff.name} - ${staff.card_no}`,
                          }))}
                        onChange={(selectedOption) => {
                          setInputs((prev) => ({
                            ...prev,
                            counter_id: selectedOption.value,
                          }));
                        }}
                        placeholder="Select Counter Staff"
                      />
                    </div>
                    <div className="col-lg">
                      <label>Status</label>
                      <select
                        className="form-control"
                        name="status"
                        value={inputs.status || ""}
                        onChange={handleChange}
                      >
                        <option value="active">Active</option>
                        <option value="returned">Returned</option>
                      </select>
                    </div>
                  </div>
                )}

                <div className="row mt-3">
                  <div className="col-lg">
                    <label>Description</label>
                    <textarea
                      className="form-control"
                      name="description"
                      value={inputs.description || ""}
                      onChange={handleChange}
                      placeholder="Enter Description"
                      rows="3"
                    ></textarea>
                  </div>
                </div>

                <div className="row mt-3">
                  <div className="col-3">
                    <button
                      className="btn btn-primary btn-block mg-b-10"
                      onClick={formSubmit}
                    >
                      <i className="fa fa-send mg-r-10"></i> Update
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DepositEdit;